import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFinance } from "@/context/FinanceContext";
import { useStock } from "@/context/StockContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const SupplierManagement: React.FC = () => {
  const { t } = useTranslation();
  const { suppliers, supplierPurchases, addSupplier, addSupplierPurchase, recordPayment, loading } = useFinance();
  const { products } = useStock();
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentPurchaseId, setPaymentPurchaseId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const [purchaseForm, setPurchaseForm] = useState({
    supplierId: "",
    productId: "",
    quantity: "",
    totalCost: "",
    amountPaid: "",
    notes: "",
    purchaseDate: new Date().toISOString().split('T')[0],
    addToStock: true,
  });

  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addSupplier(supplierForm);
      setSupplierForm({ name: "", phone: "", email: "", address: "" });
      setSupplierOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddPurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addSupplierPurchase({
        supplierId: purchaseForm.supplierId,
        productId: purchaseForm.productId,
        quantity: parseInt(purchaseForm.quantity),
        totalCost: parseFloat(purchaseForm.totalCost),
        amountPaid: parseFloat(purchaseForm.amountPaid),
        notes: purchaseForm.notes,
        purchaseDate: new Date(purchaseForm.purchaseDate),
        addToStock: purchaseForm.addToStock,
      });
      setPurchaseForm({
        supplierId: "",
        productId: "",
        quantity: "",
        totalCost: "",
        amountPaid: "",
        notes: "",
        purchaseDate: new Date().toISOString().split('T')[0],
        addToStock: true,
      });
      setPurchaseOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) return;
    try {
      await recordPayment(paymentPurchaseId, parseFloat(paymentAmount));
      setPaymentOpen(false);
      setPaymentPurchaseId("");
      setPaymentAmount("");
    } catch (error) {
      console.error(error);
    }
  };

  const totalOwed = supplierPurchases.reduce((sum, p) => sum + p.amountOwed, 0);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="suppliers">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="suppliers">{t("finance.suppliers")}</TabsTrigger>
          <TabsTrigger value="purchases">{t("finance.purchases")}</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t("finance.suppliers")}</h2>
            <Dialog open={supplierOpen} onOpenChange={setSupplierOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" />{t("finance.addSupplier")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("finance.addSupplier")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSupplier} className="space-y-4">
                  <div>
                    <Label>{t("finance.name")} *</Label>
                    <Input value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t("finance.phone")}</Label>
                    <Input value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t("finance.email")}</Label>
                    <Input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t("finance.address")}</Label>
                    <Input value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} />
                  </div>
                  <Button type="submit" className="w-full">{t("finance.addSupplier")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? <p>{t("common.loading")}</p> : suppliers.length === 0 ? <p className="text-gray-500">{t("finance.noSuppliers")}</p> : (
                <div className="space-y-2">
                  {suppliers.map((supplier) => (
                    <div key={supplier.id} className="p-3 border rounded">
                      <p className="font-medium">{supplier.name}</p>
                      {supplier.phone && <p className="text-sm text-gray-600">{supplier.phone}</p>}
                      {supplier.email && <p className="text-sm text-gray-600">{supplier.email}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="purchases" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">{t("finance.purchases")}</h2>
              <p className="text-sm text-red-600">{t("finance.totalOwed")}: {totalOwed.toFixed(2)} DT</p>
            </div>
            <Dialog open={purchaseOpen} onOpenChange={setPurchaseOpen}>
              <DialogTrigger asChild>
                <Button><ShoppingBag className="h-4 w-4 mr-2" />{t("finance.recordPurchase")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("finance.recordPurchase")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPurchase} className="space-y-4">
                  <div>
                    <Label>{t("finance.supplier")} *</Label>
                    <Select value={purchaseForm.supplierId} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, supplierId: v })}>
                      <SelectTrigger><SelectValue placeholder={t("finance.supplier")} /></SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("finance.product")} *</Label>
                    <Select value={purchaseForm.productId} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, productId: v })}>
                      <SelectTrigger><SelectValue placeholder={t("finance.product")} /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("finance.quantity")} *</Label>
                    <Input type="number" value={purchaseForm.quantity} onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t("finance.totalCost")} (DT) *</Label>
                    <Input type="number" step="0.01" value={purchaseForm.totalCost} onChange={(e) => setPurchaseForm({ ...purchaseForm, totalCost: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t("finance.amountPaid")} (DT) *</Label>
                    <Input type="number" step="0.01" value={purchaseForm.amountPaid} onChange={(e) => setPurchaseForm({ ...purchaseForm, amountPaid: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t("finance.purchaseDate")} *</Label>
                    <Input type="date" value={purchaseForm.purchaseDate} onChange={(e) => setPurchaseForm({ ...purchaseForm, purchaseDate: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t("finance.notes")}</Label>
                    <Input value={purchaseForm.notes} onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })} />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="addToStock" checked={purchaseForm.addToStock} onChange={(e) => setPurchaseForm({ ...purchaseForm, addToStock: e.target.checked })} className="h-4 w-4" />
                    <Label htmlFor="addToStock" className="cursor-pointer">{t("finance.addToStock")}</Label>
                  </div>
                  <Button type="submit" className="w-full">{t("finance.recordPurchase")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? <p>{t("common.loading")}</p> : supplierPurchases.length === 0 ? <p className="text-gray-500">{t("finance.noPurchases")}</p> : (
                <div className="space-y-2">
                  {supplierPurchases.map((purchase) => {
                    const product = products.find(p => p.id === purchase.productId);
                    return (
                      <div key={purchase.id} className="p-3 border rounded">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{purchase.supplier?.name}</p>
                            <p className="text-sm text-gray-600">{product?.name || 'Unknown Product'}</p>
                            <p className="text-sm text-gray-600">Qty: {purchase.quantity} • {purchase.purchaseDate.toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{purchase.totalCost.toFixed(2)} DT</p>
                            <p className="text-sm text-gray-600">{t("finance.paid")}: {purchase.amountPaid.toFixed(2)} DT</p>
                            {purchase.amountOwed > 0 && (
                              <>
                                <p className="text-sm text-red-600">{t("finance.owed")}: {purchase.amountOwed.toFixed(2)} DT</p>
                                <Button size="sm" variant="outline" className="mt-1" onClick={() => { setPaymentPurchaseId(purchase.id); setPaymentAmount(""); setPaymentOpen(true); }}>{t("finance.pay")}</Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("finance.recordPayment")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>{t("finance.paymentAmount")} (DT) *</Label>
              <Input type="number" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full">{t("finance.recordPayment")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierManagement;
